import { useMemo } from "react";
import { Card, CardGroup, OverlayTrigger, Tooltip } from "react-bootstrap";

import { withJobTrackingData } from "../job-tracking/components/withJobTrackingData";
import { BomComponent } from "../job-tracking/queries/tracking-data/bom-by-component";
import { PriorityOption, priorityOptionColors, priorityOptions } from "./purchasing-priority-utils";
import { PurchasingPriorityBadge } from "./PurchasingPriorityBadge";

/**
 * Renders a component showing a summary of the labour associated with each purchasing priority/stage
 */
export const PurchasingPriorityLabourSummary: React.FC = withJobTrackingData(({ bomComponents }) => {
  const labourHoursByPriority = useMemo(() => calculateLabourByPriority(bomComponents), [bomComponents]);

  return (
    <CardGroup className="text-nowrap">
      {priorityOptions.map((priority) => (
        <PurchasingPriorityLabourCard
          key={priority}
          priority={priority}
          labourHours={labourHoursByPriority[priority]!.labourHours}
          numAssemblies={labourHoursByPriority[priority]!.numAssemblies}
        />
      ))}
    </CardGroup>
  );
});

const PurchasingPriorityLabourCard: React.FC<{
  priority: PriorityOption;
  labourHours: number;
  numAssemblies: number;
}> = ({ priority, labourHours, numAssemblies }) => {
  return (
    <Card
      key={priority}
      className="col-auto text-dark"
      style={{ backgroundColor: priorityOptionColors[priority], flexBasis: "auto" }}
    >
      <Card.Body className="py-0 px-2">
        <OverlayTrigger
          overlay={(props) => (
            <Tooltip {...props} style={{ ...props.style, "--bs-tooltip-max-width": "400px" }}>
              <b>{labourHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}</b> build hours for{" "}
              {numAssemblies} assemblies at priority <PurchasingPriorityBadge priority={priority} />
            </Tooltip>
          )}
        >
          <Card.Title className="d-inline">
            {labourHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hours
          </Card.Title>
        </OverlayTrigger>
      </Card.Body>
    </Card>
  );
};

function calculateLabourByPriority(bomComponents: BomComponent[]) {
  // Filter the BOM components to only those that need to be purchased
  const labourComponents = bomComponents.filter((component) => component.item.type === "Labor");

  // Get a list of all assemblies that will be built at each priority level
  const componentsToBuild = bomComponents.filter((component) => component.item.type === "Assembly");
  const numAssembliesByPriority = priorityOptions.map(
    (priority) =>
      componentsToBuild.filter((component) => component.quantitiesByPriority.get(priority)!.nonInventoryQuantity)
        .length,
  );

  // Calculate the total labour hours for each priority option
  // This is a cumulative sum (i.e. stage 2 includes all the costs for stage 1)
  const totalLabourHoursByPriority = priorityOptions.map((priority) =>
    labourComponents.reduce(
      (total, component) => total + component.quantitiesByPriority.get(priority)!.nonInventoryQuantity,
      0,
    ),
  );

  // Calculate the total, non-cumulative, number of hours and assemblies for each priority
  return Object.fromEntries(
    priorityOptions.map((priority, index) => {
      const totalHours = totalLabourHoursByPriority[index]!;
      const labourHours = index === 0 ? totalHours : totalHours - totalLabourHoursByPriority[index - 1]!;
      const numAssemblies =
        index === 0 ?
          numAssembliesByPriority[index]!
        : numAssembliesByPriority[index]! - numAssembliesByPriority[index - 1]!;
      return [priority, { labourHours, numAssemblies }];
    }),
  );
}
